import { useState } from "react";

import { Button, Icon, Modal, Skeleton } from "@web-app/components";
import { Attachment } from "@web-app/types";

const AG_GRID_STOP_PROPAGATION_FLAG = "__ag_Grid_Stop_Propagation";

const preventGridRowSelection = (event: Event) => {
  (event as Event & Record<typeof AG_GRID_STOP_PROPAGATION_FLAG, boolean>)[
    AG_GRID_STOP_PROPAGATION_FLAG
  ] = true;
};

type PODCellData = {
  attachments?: Attachment[] | null;
  entity: "order" | "route";
  hasAttachments?: boolean;
};

type AttachmentPreviewProps = {
  attachment: Attachment;
  index: number;
};

function AttachmentPreview({ attachment, index }: AttachmentPreviewProps) {
  const [imageLoadState, setImageLoadState] = useState<
    "error" | "loaded" | "loading"
  >("loading");

  return (
    <div className="relative flex h-56 w-56 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 p-2">
      {imageLoadState === "loading" && (
        <Skeleton height="h-48" width="w-full" text="Loading..." />
      )}
      <img
        src={attachment.url}
        alt={`Attachment ${index + 1}`}
        className={
          imageLoadState === "loaded"
            ? "max-h-56 max-w-full object-contain"
            : "hidden"
        }
        onError={() => setImageLoadState("error")}
        onLoad={() => setImageLoadState("loaded")}
      />
      {imageLoadState === "error" && (
        <span className="text-sm text-gray-500">
          Unable to load this attachment.
        </span>
      )}
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-white text-xs text-gray-700 shadow-md hover:bg-gray-50"
        aria-label={`Download attachment ${index + 1}`}
        title="Download attachment"
      >
        <Icon name="download" className="h-4 w-4" />
      </a>
    </div>
  );
}

export default function PODCell({ data }: { data?: PODCellData }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const attachments = data?.entity === "order" ? (data.attachments ?? []) : [];

  if (data?.entity === "route") {
    return data.hasAttachments ? null : <span>N/A</span>;
  }

  if (!attachments.length) {
    return <span>N/A</span>;
  }

  return (
    <>
      <div
        className="flex flex-wrap gap-1 py-1"
        onClickCapture={(event) => preventGridRowSelection(event.nativeEvent)}
        onMouseDownCapture={(event) =>
          preventGridRowSelection(event.nativeEvent)
        }
        onPointerDownCapture={(event) =>
          preventGridRowSelection(event.nativeEvent)
        }
      >
        <Button
          size="2xs"
          color="light"
          onClick={(event) => {
            event.stopPropagation();
            setIsModalOpen(true);
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          View attachments
        </Button>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Attachments"
        width={900}
      >
        <div className="flex min-h-[600px] max-h-[75vh] flex-wrap content-start gap-3 overflow-y-auto border-t border-gray-200 p-2 pt-4">
          {attachments.map((attachment, index) => (
            <AttachmentPreview
              key={attachment.code}
              attachment={attachment}
              index={index}
            />
          ))}
        </div>
      </Modal>
    </>
  );
}
